import { supabase } from './supabase';

/**
 * Asegura que la empresa tenga su marca DEFAULT en `brand_profiles`.
 *
 * Hasta ahora el onboarding de registro solo creaba la empresa
 * (`company_profiles`) — la marca quedaba únicamente como `company_profiles.nombre`
 * y NO se creaba la fila en `brand_profiles` (solo ~184 de 8484 empresas la
 * tenían, vía el flujo aparte "Mi marca"). Este helper la crea al registrarse.
 *
 * Es el MISMO patrón (probado en prod) que el safety-net de `BrandDescription`:
 * crea la default a partir de `company_profiles` y reasigna los productos sin
 * marca a ella.
 *
 * Garantías:
 *  - **Idempotente:** si ya existe una marca default (`is_default=true`,
 *    `is_draft=false`) no hace nada → seguro de llamar más de una vez.
 *  - **Best-effort:** cualquier error se loguea y se ignora; nunca debe romper
 *    el onboarding (el caller no necesita await-ear el resultado para navegar).
 *  - La marca arranca "pelada" (sin colores/fuentes): la identidad con IA se
 *    completa después con el flujo "Mejorá tus fotos" (`generar_identidad`), que
 *    en modo default ACTUALIZA esta misma fila (no crea otra).
 */
export async function ensureDefaultBrand(companyId: string): Promise<void> {
  if (!companyId) return;
  try {
    const { data: defaultBrand } = await supabase
      .from('brand_profiles')
      .select('id')
      .eq('id_company', companyId)
      .eq('is_default', true)
      .eq('is_draft', false)
      .limit(1)
      .maybeSingle();
    if (defaultBrand) return; // ya tiene marca default → no duplicar

    const { data: companyRow } = await supabase
      .from('company_profiles')
      .select('nombre, rubro, logo_url')
      .eq('id_company', companyId)
      .maybeSingle();

    const name =
      (companyRow as { nombre?: string } | null)?.nombre?.trim() ||
      'Marca principal';
    const rubro = (companyRow as { rubro?: string } | null)?.rubro || '';
    const logo = (companyRow as { logo_url?: string } | null)?.logo_url || null;

    const { data: created, error: insErr } = await supabase
      .from('brand_profiles')
      .insert({
        id_company: companyId,
        name,
        rubro,
        logo_url: logo,
        context: '',
        colors: [],
        is_default: true,
        is_draft: false,
      })
      .select('id')
      .single();
    if (insErr || !created?.id) {
      if (insErr) console.error('[ensureDefaultBrand] insert error:', insErr);
      return;
    }

    // Reasignar los productos sin marca a la nueva default (mismo backfill que
    // el safety-net de BrandDescription).
    await supabase
      .from('productos')
      .update({ brand_id: created.id })
      .eq('id_company', companyId)
      .is('brand_id', null);
  } catch (e) {
    console.error('[ensureDefaultBrand] error:', e);
  }
}
